/**
 * TK-22 — HIỆN MỘT CÔNG SUẤT: tự chọn W hay kW theo ĐỘ LỚN, không gắn cứng theo loại công tơ.
 * Thuần: Tổng quan, biểu đồ chi tiết tủ và `server/src/scripts/kiem-tk22.ts` đọc cùng một hàm.
 *
 * ⚠ Đầu vào luôn là W. Ai đang cầm một số kW (công tơ MFM) thì nhân 1 000 TRƯỚC khi gọi —
 *   `heSoVeW()` dưới đây. Hai đơn vị không bao giờ gặp nhau trong một phép cộng.
 */

/** Từ ngưỡng này trở lên thì hiện kW. */
export const NGUONG_KW = 1000;

export type DonViHien = 'W' | 'kW';

/** Đơn vị đẹp cho một dải số W (vd: max của một trục, hay một tổng). */
export function donViHien(w: number): DonViHien {
  return Math.abs(w) >= NGUONG_KW ? 'kW' : 'W';
}

/** Đổi một số W sang đơn vị hiện. */
export function theoDonVi(w: number, dv: DonViHien): number {
  return dv === 'kW' ? w / 1000 : w;
}

/** Số chữ thập phân hợp với đơn vị: kW 2 chữ (0,01 kW = 10 W), W 1 chữ. */
export const CHU_SO: Record<DonViHien, number> = { W: 1, kW: 2 };

/** Đơn vị công suất mà công tơ GỬI LÊN (theo model) ⇒ hệ số nhân về W. `null` = chưa rõ. */
export function heSoVeW(donViCongTo: 'W' | 'kW' | null | undefined): number | null {
  if (donViCongTo === 'W') return 1;
  if (donViCongTo === 'kW') return 1000;
  return null;
}

/**
 * Một giá trị công suất theo đơn vị CÔNG TƠ gửi ⇒ số + đơn vị để hiện, W/kW theo độ lớn.
 * Công tơ chưa rõ ⇒ giữ nguyên số, `donVi = null` (KHÔNG gắn đơn vị đoán — luật TK-19/TK-20).
 */
export function hienCongSuat(giaTri: number, donViCongTo: 'W' | 'kW' | null | undefined):
  { tri: number; donVi: DonViHien | null; chuSo: number } {
  const heSo = heSoVeW(donViCongTo);
  if (heSo === null) return { tri: giaTri, donVi: null, chuSo: 1 };
  const w = giaTri * heSo;
  const dv = donViHien(w);
  return { tri: theoDonVi(w, dv), donVi: dv, chuSo: CHU_SO[dv] };
}

/**
 * TK-23 — dòng phụ của ô "Công suất hiện tại" (Tổng quan). Thuần, để cổng chạy thật gọi CÙNG hàm Dashboard gọi,
 * trên phản hồi `/api/overview` thật — không dựng lại chuỗi ở chỗ khác.
 * ⛔ Tủ chưa rõ đơn vị KHÔNG được cộng, và PHẢI được nói ra: tổng thiếu mà im lặng là tổng sai.
 */
export function dongPhuTongCongSuat(o: {
  cabinets: number; soTuTinhCongSuat?: number; soTuChuaRoDonVi?: number; soTuKhongCoSoCongSuat?: number;
}): string {
  return `${o.soTuTinhCongSuat ?? 0} / ${o.cabinets} tủ`
    + (o.soTuChuaRoDonVi ? ` · ${o.soTuChuaRoDonVi} tủ chưa rõ đơn vị, không tính` : '')
    + (o.soTuKhongCoSoCongSuat ? ` · ${o.soTuKhongCoSoCongSuat} tủ không có số công suất` : '');
}
